import React, { useCallback, useEffect, useMemo, useState } from "react";
import Topbar from "../Topbar";
import AddToLibrary from "./AddToLibrary";
import { roomsApi } from "./useRoom";
import { Dialog, LibThumb, SearchIcon, useRunner, useFileUrl } from "./parts";
import { KIND_FILTERS, KIND_LABEL } from "./roomModel.mjs";
import { shortDate } from "../record/format.mjs";
import { apiBlob } from "../../api";
import { uploadPdfCover } from "./pdfCover";
import "./rooms.css";

/*
 * SALES › DECKS & EXAMPLES (Library board): one library for every prospect room.
 * Pick from it on any record and add a note for that prospect. Editing a title
 * changes it on every room; the per-prospect notes stay as written. Archiving
 * keeps it on the rooms it is already on and out of the picker.
 */
export default function LibraryPage() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(null);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [archived, setArchived] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(
    () => roomsApi.library(archived).then(({ items: rows }) => setItems(rows)).catch((e) => setErr(e.message)),
    [archived]
  );
  useEffect(() => {
    load();
  }, [load]);

  // PDFs added before covers existed get their first page, once, here in the browser.
  const covering = React.useRef(new Set());
  useEffect(() => {
    if (!items) return;
    const todo = items.filter((i) => !i.cover_file_id && !i.url && (i.files || [])[0] && i.files[0].content_type === "application/pdf" && !covering.current.has(i.id));
    if (!todo.length) return;
    (async () => {
      let changed = false;
      for (const i of todo.slice(0, 5)) {
        covering.current.add(i.id);
        try {
          const blob = await apiBlob(`/room-library/files/${i.files[0].id}`);
          const cover = await uploadPdfCover(blob, roomsApi.upload);
          if (cover) {
            await roomsApi.editLibrary(i.id, { cover_file_id: cover });
            changed = true;
          }
        } catch {
          /* a card without a picture keeps its coloured block */
        }
      }
      if (changed) load();
    })();
  }, [items, load]);

  const live = (items || []).filter((i) => archived || !i.archived_at);
  const counts = useMemo(() => {
    const c = { all: live.length };
    for (const i of live) c[i.kind] = (c[i.kind] || 0) + 1;
    return c;
  }, [live]);
  const needle = q.trim().toLowerCase();
  const shown = live.filter((i) => (kind === "all" || i.kind === kind) && (!needle || `${i.title} ${i.detail || ""}`.toLowerCase().includes(needle)));

  const archive = async (i) => {
    try {
      await roomsApi.archive(i.id, !i.archived_at);
      load();
    } catch (e) {
      setErr(e.message);
    }
  };

  return (
    <>
      <Topbar title="Decks & examples" sub="One library for every prospect room. Pick from it on any record and add a note for that prospect." />
      <div className="rm rm-page">
        <div className="rm-card rm-card--pad">
          <span className="rm-label">Add to the library</span>
          <AddToLibrary onAdded={() => load()} />
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "space-between" }}>
          <div className="rm-chips" role="group" aria-label="Filter by kind">
            {KIND_FILTERS.map((f) => (
              <button key={f.key} type="button" className={`rm-chip${kind === f.key ? " is-on" : ""}`} aria-pressed={kind === f.key} onClick={() => setKind(f.key)}>
                {f.label} {counts[f.key] || 0}
              </button>
            ))}
            <label className="rm-sub" style={{ display: "inline-flex", gap: 6, alignItems: "center", marginLeft: 8 }}>
              <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
              Show archived
            </label>
          </div>
          <label className="rm-search" style={{ width: 260, maxWidth: "100%" }}>
            <SearchIcon />
            <span className="rm-sr">Search the library</span>
            <input placeholder={`Search ${live.length} items`} value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
        </div>
        {err && <p className="rm-err">{err}</p>}

        <div className="rm-card" style={{ padding: "4px 16px 8px" }}>
          <div className="rm-tablewrap">
            <table className="rm-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Kind</th>
                  <th>Added</th>
                  <th>On rooms</th>
                  <th style={{ textAlign: "right" }}><span className="rm-sr">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {items === null && (
                  <tr>
                    <td colSpan={5} className="rm-sub">Loading…</td>
                  </tr>
                )}
                {items && shown.length === 0 && (
                  <tr>
                    <td colSpan={5} className="rm-sub">{live.length ? "Nothing matches." : "The library is empty. Drop a deck or paste a link above."}</td>
                  </tr>
                )}
                {shown.map((i) => (
                  <tr key={i.id} className={i.archived_at ? "is-archived" : ""}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <LibThumb item={i} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13.5 }}>{i.title}</div>
                          <div className="rm-sub" style={{ fontSize: 12 }}>
                            {i.url ? (
                              <a href={i.url} target="_blank" rel="noopener noreferrer">{i.link ? i.link.host : i.url} ↗</a>
                            ) : (
                              i.detail
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td><span className="rm-pill">{i.url && i.link ? i.link.label : KIND_LABEL[i.kind]}</span></td>
                    <td className="rm-sub">{shortDate(i.created_at)}</td>
                    <td>{!i.rooms || !i.rooms.length ? <span className="rm-meta">Not on any room</span> : i.rooms.length > 3 ? `${i.rooms.length} rooms` : i.rooms.join(", ")}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button type="button" className="rm-link" style={{ marginRight: 12 }} onClick={() => setEditing(i)}>Edit</button>
                      <button type="button" className="rm-link" style={{ color: "var(--rm-sub)" }} onClick={() => archive(i)}>{i.archived_at ? "Unarchive" : "Archive"}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rm-foot">Archiving keeps it on rooms it’s already on. Editing the title changes it everywhere; per-prospect notes stay as written.</div>
        </div>
      </div>
      {editing && (
        <EditLibraryDialog
          item={editing}
          onClose={(changed) => {
            setEditing(null);
            if (changed) load();
          }}
        />
      )}
    </>
  );
}

function CoverPreview({ fileId, item }) {
  const src = useFileUrl(fileId);
  return (
    <span aria-hidden="true" style={{ width: 120, height: 72, flex: "none", border: "2px solid #0A0A0A", borderRadius: 6, overflow: "hidden", background: "#F2F1EC", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#77746B" }}>
      {src ? <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} /> : item.kind_label}
    </span>
  );
}

function EditLibraryDialog({ item, onClose }) {
  const [cover, setCover] = useState(item.cover_file_id || null);
  const coverInput = React.useRef(null);
  const [title, setTitle] = useState(item.title);
  const [kind, setKind] = useState(item.kind);
  const [url, setUrl] = useState(item.url || "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = useRunner(setBusy, setErr);
  const save = () =>
    run(async () => {
      await roomsApi.editLibrary(item.id, { title, kind, ...(item.url ? { url } : {}), ...(cover !== (item.cover_file_id || null) ? { cover_file_id: cover } : {}) });
      onClose(true);
    });
  const n = (item.rooms || []).length;
  return (
    <Dialog
      eyebrow="Decks & examples"
      title="Edit library item"
      size="sm"
      onClose={() => onClose(false)}
      foot={
        <>
          <span className="rm-sub">{n ? `Changes it on ${n} ${n === 1 ? "room" : "rooms"}` : "Not on any room yet"}</span>
          {err && <span className="rm-err">{err}</span>}
          <button type="button" className="rm-btn" onClick={() => onClose(false)}>Cancel</button>
          <button type="button" className="rm-btn rm-btn--primary" disabled={busy || !title.trim()} onClick={save}>{busy ? "Saving…" : "Save"}</button>
        </>
      }
    >
      <div className="rm-field">
        <label htmlFor="rm-lt">Title</label>
        <input id="rm-lt" className="rm-input" value={title} maxLength={140} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="rm-field">
        <label htmlFor="rm-lk">Kind</label>
        <select id="rm-lk" className="rm-select" value={kind} onChange={(e) => setKind(e.target.value)}>
          {Object.entries(KIND_LABEL).map(([k, l]) => (
            <option key={k} value={k}>{l}</option>
          ))}
        </select>
      </div>
      <div className="rm-field">
        <span className="rm-field__label">Cover on the room</span>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <CoverPreview fileId={cover} item={item} />
          <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
            <button type="button" className="rm-link rm-link--strong" disabled={busy} onClick={() => coverInput.current && coverInput.current.click()}>
              {cover ? "Change cover" : "Add a cover"}
            </button>
            {cover && (
              <button type="button" className="rm-link" disabled={busy} onClick={() => setCover(null)}>Remove cover</button>
            )}
            <span className="rm-hint">An image of the first slide works best.</span>
          </div>
          <input
            ref={coverInput}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            hidden
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              if (f) run(async () => setCover((await roomsApi.upload(f)).file.id));
            }}
          />
        </div>
      </div>
      {item.url && (
        <div className="rm-field">
          <label htmlFor="rm-lu">Link</label>
          <input id="rm-lu" className="rm-input" value={url} onChange={(e) => setUrl(e.target.value)} />
        </div>
      )}
    </Dialog>
  );
}
